import { lazy, type ReactNode, Suspense, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle2, Download, FilePen, FilePlus, FileText, History } from 'lucide-react';
import { type DocumentDetail, type FileDownloadUrl } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { ErrorState, Skeleton } from '../../../components/ui/states';
import { UploadButton } from '../../../components/ui/upload-button';
import { errorMessage } from '../../../lib/error-message';
import { apiFetch } from '../../../lib/http';
import { uploadFile } from '../../../lib/upload';
import { documentApi, documentKeys } from '../api';
import { editableKindOf, editorNameOf } from '../editable-format';
import { projectDocumentStorage } from '../project-document-storage';
import { DeleteDocumentButton } from './delete-document-button';
import { RenameDocumentButton } from './rename-document-button';
import { displayDateTime } from '../../../lib/date';

const DocumentEditorDialog = lazy(() => import('../../my-files/editor/document-dialog'));
const SheetEditorDialog = lazy(() => import('../../my-files/sheet-editor/sheet-editor-dialog'));
const PptxEditorDialog = lazy(() => import('../../my-files/pptx-editor/pptx-editor-dialog'));

/**
 * Chi tiết một tài liệu: lịch sử phiên bản và workflow duyệt — APM-703,
 * bố cục lại ở APM-PDOC-e (người dùng chốt 2026-09-28).
 *
 * ## Mỗi phiên bản một dòng, mỗi dòng một trạng thái
 *
 * Duyệt vẫn chỉ áp cho **bản mới nhất** (API không đổi): các nút Gửi duyệt /
 * Duyệt / Trả lại cùng ô ghi chú nằm ngay trên dòng của bản ấy, nên không ai
 * phải đoán nút ở đầu trang đang nói về bản nào. Bản cũ mang nhãn "Đã duyệt"
 * (con trỏ bản đã duyệt) hoặc "Bản cũ".
 *
 * ## Không còn sửa tại chỗ
 *
 * Trước đây nội dung bản mới nhất là một `<textarea>` luôn mở kèm nút "Lưu thành
 * phiên bản mới". Giờ muốn có bản mới thì bấm **Tạo phiên bản mới**, và chỉ lúc
 * ấy mới hiện: ô "Có gì thay đổi so với bản trước", nút **Mở để sửa** (hộp soạn,
 * mỗi lần Lưu là một phiên bản mang ghi chú ấy) và nút **Tải bản mới lên**. Xem
 * nội dung thì ở popup xem trước (`DocumentPreviewDialog`).
 */
export function DocumentDetailPanel({
  documentId,
  canEdit,
  canApprove,
  onDeleted,
}: {
  documentId: string;
  canEdit: boolean;
  canApprove: boolean;
  onDeleted: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const changeNoteRef = useRef(changeNote);
  changeNoteRef.current = changeNote;
  const [note, setNote] = useState('');
  const [showing, setShowing] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const key = documentKeys.detail(documentId);
  const document = useQuery({ queryKey: key, queryFn: () => documentApi.detail(documentId) });

  const shownVersion = useQuery({
    queryKey: [...key, 'version', showing],
    queryFn: () => documentApi.version(documentId, showing!),
    enabled: showing !== null,
  });

  const fail = (error: unknown) => toast.error(errorMessage(error, t));
  const absorb = (updated: DocumentDetail) => {
    queryClient.setQueryData(key, updated);
    // Danh sách hiện trạng thái và số phiên bản, nên nó cũng cũ theo.
    void queryClient.invalidateQueries({ queryKey: ['document'] });
  };
  const created = (updated: DocumentDetail) => {
    absorb(updated);
    toast.success(t('documents.versionAdded', { no: updated.latestVersionNo }));
  };

  const addVersion = useMutation({
    mutationFn: (fileId: string) =>
      documentApi.addVersion(documentId, {
        fileId,
        ...(changeNote.trim() ? { changeNote: changeNote.trim() } : {}),
      }),
    onSuccess: (updated) => {
      created(updated);
      setCreating(false);
      setChangeNote('');
    },
    onError: fail,
  });

  const transition = useMutation({
    mutationFn: (action: 'submit' | 'approve' | 'reject') =>
      documentApi.transition(documentId, action, note.trim() ? { note: note.trim() } : {}),
    onSuccess: (updated) => {
      absorb(updated);
      setNote('');
    },
    onError: fail,
  });

  const upload = useMutation({
    mutationFn: async (file: File) =>
      uploadFile(file, { projectId: document.data!.projectId, documentId }),
    onSuccess: (fileId) => addVersion.mutate(fileId),
    onError: fail,
  });

  const projectId = document.data?.projectId;
  /*
    Lần Lưu đầu trong hộp soạn là phiên bản mới, mang ghi chú đang gõ ở ô "Có
    gì thay đổi" — đọc qua ref lúc lưu, không phải lúc dựng adapter. Các lần
    Lưu sau ghi đè chính bản ấy (APM-PDOC-f).
  */
  const storage = useMemo(
    () =>
      projectId
        ? projectDocumentStorage(projectId, created, {
            changeNote: () => changeNoteRef.current.trim() || undefined,
            firstSaveAsVersion: true,
          })
        : undefined,
    // `created` chỉ đóng trên `key`, `queryClient` và `t`, cả ba cố định theo tài liệu.
    [projectId, documentId],
  );

  if (document.isPending) return <Skeleton className="h-64 w-full" />;
  if (document.isError) {
    return (
      <ErrorState
        message={errorMessage(document.error, t)}
        onRetry={() => void document.refetch()}
      />
    );
  }

  const doc = document.data;
  // "Mở để sửa" — chỉ khi hộp soạn đọc được bản mới nhất (APM-PDOC-c).
  const editable = editableKindOf(doc);
  const busy = upload.isPending || addVersion.isPending;

  async function download(fileId: string): Promise<void> {
    const { url } = await apiFetch<FileDownloadUrl>(`/files/${fileId}/url`);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="flex flex-col gap-3">
      <header className="flex flex-wrap items-center gap-2">
        <FileText className="size-4 text-muted-foreground" />
        <span className="text-sm font-medium">{doc.title}</span>
        {canEdit ? <RenameDocumentButton document={doc} projectId={doc.projectId} /> : null}
        <span className="rounded-full border border-border px-2 py-0.5 text-xs">
          {t(`documents.kind.${doc.kind}`)}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('documents.versionCount', { count: doc.latestVersionNo })}
        </span>
        {doc.taskKey && (
          <span className="font-mono text-xs text-muted-foreground">{doc.taskKey}</span>
        )}

        {canEdit && !creating && (
          <Button size="sm" className="ml-auto" onClick={() => setCreating(true)}>
            <FilePlus aria-hidden />
            {t('documents.newVersion')}
          </Button>
        )}
        {canApprove && (
          <span className={canEdit && !creating ? undefined : 'ml-auto'}>
            <DeleteDocumentButton document={doc} projectId={doc.projectId} onDeleted={onDeleted} />
          </span>
        )}
      </header>

      {/* ── Tạo phiên bản mới — chỉ hiện khi bấm nút ─────────────────────── */}
      {creating ? (
        <section
          aria-label={t('documents.newVersion')}
          className="flex flex-col gap-2 rounded-DEFAULT border border-border p-3"
        >
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('documents.changeNote')}</span>
            <input
              className="h-8 rounded-control border border-border-strong bg-background px-2 text-sm"
              placeholder={t('documents.changeNotePlaceholder')}
              value={changeNote}
              onChange={(event) => setChangeNote(event.target.value)}
            />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {editable ? (
              <Button size="sm" disabled={busy} onClick={() => setEditing(true)}>
                <FilePen aria-hidden />
                {t('documents.openEditor')}
              </Button>
            ) : null}
            <input
              ref={fileInput}
              type="file"
              className="hidden"
              data-testid="document-version-input"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) upload.mutate(file);
                event.target.value = '';
              }}
            />
            <UploadButton
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => fileInput.current?.click()}
            >
              {busy ? t('documents.uploading') : t('documents.uploadVersion')}
            </UploadButton>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setCreating(false);
                setChangeNote('');
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {editable ? t('documents.newVersionHelp') : t('documents.newVersionUploadOnly')}
          </p>
        </section>
      ) : null}

      {editing && editable && storage ? (
        <Suspense fallback={null}>
          {(() => {
            const onClose = (): void => {
              setEditing(false);
              // Đã lưu ít nhất một bản ⇒ việc "tạo phiên bản mới" này xong.
              if (
                queryClient.getQueryData<DocumentDetail>(key)?.latestVersionNo !==
                doc.latestVersionNo
              ) {
                setCreating(false);
                setChangeNote('');
              }
            };
            return editable.kind === 'sheet' ? (
              // Bảng tính (APM-XLE-d): hộp soạn Univer, cùng nơi lưu.
              <SheetEditorDialog
                item={{ id: doc.id, name: editable.filename }}
                storage={storage}
                onClose={onClose}
              />
            ) : editable.kind === 'pptx' ? (
              // PowerPoint (APM-PPX-i): hộp soạn pptx, cùng nơi lưu.
              <PptxEditorDialog
                item={{ id: doc.id, name: editable.filename }}
                storage={storage}
                onClose={onClose}
              />
            ) : (
              <DocumentEditorDialog
                target={{
                  kind: 'edit',
                  item: {
                    id: doc.id,
                    name: editable.filename ?? editorNameOf(doc, editable.format),
                  },
                }}
                storage={storage}
                onClose={onClose}
              />
            );
          })()}
        </Suspense>
      ) : null}

      {/* ── Lịch sử phiên bản: trạng thái từng bản, duyệt ở bản mới nhất ──── */}
      <section>
        <h4 className="mb-1 flex items-center gap-1 text-sm font-medium">
          <History className="size-4" />
          {t('documents.history')}
        </h4>

        <ul className="flex flex-col gap-1">
          {doc.versions.map((version) => {
            const latest = version.versionNo === doc.latestVersionNo;
            const status: VersionStatus = version.isApproved
              ? 'approved'
              : latest
                ? doc.status
                : 'superseded';
            return (
              <li
                key={version.id}
                className="flex flex-col gap-2 rounded-DEFAULT bg-muted/30 px-2 py-1.5 text-xs"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="font-mono hover:underline"
                    onClick={() =>
                      setShowing(showing === version.versionNo ? null : version.versionNo)
                    }
                  >
                    v{version.versionNo}
                  </button>
                  <VersionStatusBadge status={status} />
                  <span className="min-w-40 flex-1">
                    {version.changeNote ?? t('documents.noChangeNote')}
                  </span>
                  <span className="text-muted-foreground">
                    {version.createdByName ?? t('documents.unknownAuthor')} ·{' '}
                    {displayDateTime(version.createdAt)}
                  </span>
                  {version.fileId && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void download(version.fileId!)}
                    >
                      <Download />
                      {version.filename}
                    </Button>
                  )}
                  {/*
                    Tình trạng đọc nội dung tệp — APM-DOC-d. `extracted` không
                    có nhãn: nó là trạng thái mong đợi.
                  */}
                  {version.extractionStatus !== null &&
                    version.extractionStatus !== 'extracted' && (
                      <span
                        className="text-xs text-muted-foreground"
                        title={version.extractionNote ?? undefined}
                      >
                        {t(`documents.extraction.${version.extractionStatus}`)}
                      </span>
                    )}
                </div>

                {latest ? (
                  <ReviewControls
                    status={doc.status}
                    canEdit={canEdit}
                    canApprove={canApprove}
                    note={note}
                    onNote={setNote}
                    pending={transition.isPending}
                    onAction={(action) => transition.mutate(action)}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>

        {showing !== null && (
          <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded-DEFAULT border border-border p-2 font-mono text-xs">
            {shownVersion.isPending
              ? t('common.loading')
              : (shownVersion.data?.content ?? t('documents.fileOnly'))}
          </pre>
        )}
      </section>
    </div>
  );
}

/** Trạng thái của **một** phiên bản: bản mới nhất theo tài liệu; bản cũ đã duyệt hay đã bị thay. */
type VersionStatus = DocumentDetail['status'] | 'superseded';

function VersionStatusBadge({ status }: { status: VersionStatus }): ReactNode {
  const { t } = useTranslation();
  if (status !== 'superseded') return <StatusBadge status={status} />;
  return (
    <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
      {t('documents.versionStatus.superseded')}
    </span>
  );
}

/**
 * Duyệt bản mới nhất, ngay trên dòng của nó — spec mục 7: draft → review →
 * approved. Trả lại **bắt buộc** kèm lý do (server cũng chặn; đây chỉ để nút
 * không mời người dùng bấm vào một lỗi).
 */
function ReviewControls({
  status,
  canEdit,
  canApprove,
  note,
  onNote,
  pending,
  onAction,
}: {
  status: DocumentDetail['status'];
  canEdit: boolean;
  canApprove: boolean;
  note: string;
  onNote: (note: string) => void;
  pending: boolean;
  onAction: (action: 'submit' | 'approve' | 'reject') => void;
}): ReactNode {
  const { t } = useTranslation();
  const canSubmit = canEdit && status === 'draft';
  const canReview = canApprove && status === 'review';

  if (status === 'approved') {
    return <p className="text-xs text-muted-foreground">{t('documents.approvedHint')}</p>;
  }
  if (!canSubmit && !canReview) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        className="h-8 min-w-60 flex-1 rounded-control border border-border-strong bg-background px-2 text-sm"
        placeholder={t('documents.notePlaceholder')}
        aria-label={t('documents.note')}
        value={note}
        onChange={(event) => onNote(event.target.value)}
      />
      {canSubmit && (
        <Button size="sm" disabled={pending} onClick={() => onAction('submit')}>
          {t('documents.submit')}
        </Button>
      )}
      {canReview && (
        <>
          <Button size="sm" disabled={pending} onClick={() => onAction('approve')}>
            <CheckCircle2 aria-hidden />
            {t('documents.approve')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={pending || note.trim().length === 0}
            onClick={() => onAction('reject')}
          >
            {t('documents.reject')}
          </Button>
        </>
      )}
    </div>
  );
}

export function StatusBadge({ status }: { status: DocumentDetail['status'] }): ReactNode {
  const { t } = useTranslation();

  const tone =
    status === 'approved'
      ? 'border-rag-green/40 bg-rag-green/10 text-rag-green'
      : status === 'review'
        ? 'border-rag-amber/40 bg-rag-amber/10 text-rag-amber'
        : 'border-border bg-muted';

  return (
    <span className={`rounded-full border px-2 py-0.5 text-xs ${tone}`}>
      {t(`documents.status.${status}`)}
    </span>
  );
}
